import { useCallback, useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  ActionIcon,
  Badge,
  Divider,
  Loader,
  Alert,
  ScrollArea,
} from '@mantine/core';
import { IconPencil, IconTrash, IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import logger from '@ayocore/platform/common/helpers/logger';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { usePhotoNameTemplateRepository } from '../../rpc';
import { makePhraseLabel } from './inspectionTemplates';
import { PhotoNameTemplateFormModal } from './PhotoNameTemplateFormModal';

/** Шаблон имени фото (подмножество строки mws_photo_name_templates). */
interface PhotoNameTemplateRow {
  id: number;
  label: string;
  value: string;
  /** true — системный (из сида), не удаляется из UI; false — пользовательский. */
  isSystem: boolean;
}

export interface PhotoNameTemplatesModalProps {
  /** Вид работ, чьи шаблоны редактируем. Опционально для useModal (weak-type). */
  workTypeId?: number;
  /** Вызывается после любого изменения набора (родитель перечитывает список). */
  onChanged?: () => void;
}

/**
 * Менеджер шаблонов значения {object} имени фото по виду работ.
 *
 * Открывается из фото-таба инспекции (кнопка-шестерёнка рядом с «＋»). Список
 * шаблонов вида работ с инлайн-редактированием (подпись + значение) и удалением.
 * Системные (is_system) можно редактировать, но не удалять (помечены бейджем);
 * пользовательские — полный CRUD.
 *
 * Обёртки <Modal> нет — компонент открывается через useModal.
 */
export function PhotoNameTemplatesModal({ workTypeId, onChanged }: PhotoNameTemplatesModalProps) {
  const repo = usePhotoNameTemplateRepository();
  const confirm = useConfirm();
  const formModal = useModal(PhotoNameTemplateFormModal);

  const [templates, setTemplates] = useState<PhotoNameTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Инлайн-редактирование существующего шаблона.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');

  const reload = useCallback(async () => {
    if (workTypeId == null) {
      setLoading(false);
      setLoadError('Вид работ не указан');
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const list = await repo.filterByWorkType(workTypeId).orderBy('id', 'asc').fetch();
      setTemplates(
        list.map(t => ({ id: t.id, label: t.label, value: t.value, isSystem: t.is_system }))
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Ошибка загрузки');
      logger.error(err, '[mws] PhotoNameTemplatesModal.reload');
    } finally {
      setLoading(false);
    }
  }, [repo, workTypeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const notifyError = (err: unknown, fallback: string) => {
    notifications.show({
      title: 'Ошибка',
      message: err instanceof Error ? err.message : fallback,
      color: 'red',
    });
  };

  const startEdit = (row: PhotoNameTemplateRow) => {
    setEditingId(row.id);
    setEditLabel(row.label);
    setEditValue(row.value);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
    setEditValue('');
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const value = editValue.trim();
    if (!value) return;
    const label = editLabel.trim() || makePhraseLabel(value);
    try {
      setBusy(true);
      await repo.update(editingId, { label, value });
      cancelEdit();
      await reload();
      onChanged?.();
    } catch (err) {
      notifyError(err, 'Не удалось сохранить шаблон');
      logger.error(err, '[mws] PhotoNameTemplatesModal.saveEdit');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: PhotoNameTemplateRow) => {
    const ok = await confirm(`Удалить шаблон «${row.label}»?`, {
      title: 'Удаление шаблона',
      confirmLabel: 'Удалить',
    });
    if (!ok) return;
    try {
      setBusy(true);
      await repo.deleteById(row.id);
      await reload();
      onChanged?.();
    } catch (err) {
      notifyError(err, 'Не удалось удалить шаблон');
      logger.error(err, '[mws] PhotoNameTemplatesModal.remove');
    } finally {
      setBusy(false);
    }
  };

  // Добавление шаблона — общая модалка (та же, что «＋» в фото-табе).
  const openAdd = () => {
    if (workTypeId == null) return;
    formModal.open(
      {
        workTypeId,
        onSuccess: () => {
          formModal.close();
          void reload();
          onChanged?.();
        },
        title: 'Новый шаблон',
      },
      { size: 'md' }
    );
  };

  if (loading) {
    return (
      <Group justify='center' py='lg'>
        <Loader size='sm' />
      </Group>
    );
  }

  if (loadError) {
    return (
      <Alert color='red' title='Ошибка'>
        {loadError}
      </Alert>
    );
  }

  return (
    <Stack gap='sm'>
      {templates.length === 0 && (
        <Text size='sm' c='dimmed'>
          Для этого вида работ шаблонов пока нет.
        </Text>
      )}

      {/* Список скроллится внутри — действия ниже остаются на виду. */}
      <ScrollArea.Autosize mah={420} type='auto' offsetScrollbars>
        <Stack gap={0} pr='xs'>
          {templates.map((row, index) => (
            <div key={row.id}>
              {index > 0 && <Divider my={6} />}
              {editingId === row.id ? (
                <Stack gap={6}>
                  <TextInput
                    size='xs'
                    label='Название'
                    placeholder='Короткий тайтл для кнопки (пусто → из текста)'
                    value={editLabel}
                    onChange={e => setEditLabel(e.currentTarget.value)}
                    disabled={busy}
                  />
                  <TextInput
                    size='xs'
                    label='Текст фразы'
                    value={editValue}
                    onChange={e => setEditValue(e.currentTarget.value)}
                    disabled={busy}
                  />
                  <Group gap='xs' justify='flex-end'>
                    <Button
                      size='compact-xs'
                      variant='default'
                      leftSection={<IconX size={14} />}
                      disabled={busy}
                      onClick={cancelEdit}
                    >
                      Отмена
                    </Button>
                    <Button
                      size='compact-xs'
                      variant='light'
                      leftSection={<IconCheck size={14} />}
                      disabled={busy || !editValue.trim()}
                      onClick={() => void saveEdit()}
                    >
                      Сохранить
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Group justify='space-between' align='flex-start' wrap='nowrap' gap='sm'>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} align='center'>
                      <Text fw={500} size='sm'>
                        {row.label}
                      </Text>
                      {row.isSystem && (
                        <Badge size='xs' variant='light' color='gray'>
                          системный
                        </Badge>
                      )}
                    </Group>
                    <Text size='xs' c='dimmed' fs='italic'>
                      {row.value}
                    </Text>
                  </div>
                  <Group gap={4} wrap='nowrap'>
                    <ActionIcon
                      size='sm'
                      variant='subtle'
                      color='gray'
                      disabled={busy}
                      aria-label='Редактировать'
                      onClick={() => startEdit(row)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    {!row.isSystem && (
                      <ActionIcon
                        size='sm'
                        variant='subtle'
                        color='red'
                        disabled={busy}
                        aria-label='Удалить'
                        onClick={() => void remove(row)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              )}
            </div>
          ))}
        </Stack>
      </ScrollArea.Autosize>

      <Divider />

      <Group>
        <Button
          size='compact-xs'
          variant='light'
          leftSection={<IconPlus size={14} />}
          disabled={busy || workTypeId == null}
          onClick={openAdd}
        >
          Добавить шаблон
        </Button>
      </Group>
    </Stack>
  );
}
